import React, { useEffect, useRef } from 'react';
import './AdSense.css';

/**
 * AdSense Component
 * 
 * Usage:
 * <AdSense 
 *   adSlot="1234567890" 
 *   adFormat="auto" 
 *   fullWidthResponsive={true}
 *   style="display:block"
 * />
 * 
 * To get your ad slot ID:
 * 1. Sign up for Google AdSense: https://www.google.com/adsense
 * 2. Create an ad unit in your AdSense account
 * 3. Copy the ad unit ID (format: ca-pub-XXXXXXXXXXXXXXXX/YYYYYYYYYY)
 * 4. Replace the adSlot prop with your ad unit ID
 */

const AdSense = ({ 
  adSlot, 
  adFormat = 'auto', 
  fullWidthResponsive = true,
  style = 'display:block',
  className = '',
  adClient = null // Optional: override default ad client
}) => {
  const adRef = useRef(null);
  const adClientId = adClient || process.env.REACT_APP_ADSENSE_CLIENT_ID || 'ca-pub-2166000933732058';

  // Check if adSlot is valid (not a placeholder)
  const isValidAdSlot = (slot) => {
    if (!slot) return false;
    // Reject common placeholder values
    const placeholders = ['1234567890', '0000000000', 'test', 'placeholder'];
    if (placeholders.includes(slot.toString())) return false;
    // Ad slot IDs are typically numeric strings of 8-10 digits
    // Allow alphanumeric for flexibility but reject obvious placeholders
    return slot.toString().length >= 8 && !slot.toString().startsWith('12345');
  };

  const hasValidAdSlot = isValidAdSlot(adSlot);

  useEffect(() => {
    // Only load ads in production and if adSlot is valid
    if (!hasValidAdSlot || process.env.NODE_ENV === 'development') {
      return;
    }

    try {
      // Check if AdSense script is already loaded
      if (window.adsbygoogle && window.adsbygoogle.loaded) {
        try {
          (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {
          console.error('AdSense error:', e);
        }
      } else {
        // Load AdSense script if not already loaded
        const script = document.createElement('script');
        script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${adClientId}`;
        script.async = true;
        script.crossOrigin = 'anonymous';
        script.onload = () => {
          try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
          } catch (e) {
            console.error('AdSense error:', e);
          }
        };
        document.head.appendChild(script);
      }
    } catch (error) {
      console.error('Error loading AdSense:', error);
    }
  }, [adSlot, adClientId, hasValidAdSlot]);

  // Don't render ads if no valid adSlot provided or in development
  // Return null to avoid rendering anything (including placeholders) in production
  // This prevents Google from detecting ad requests without valid ad slots
  if (!hasValidAdSlot || process.env.NODE_ENV === 'development') {
    // In development, show placeholder for testing
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className={`adsense-placeholder ${className}`}>
          <div className="adsense-placeholder-content">
            <p>Ad Space</p>
            <small>AdSense will appear here in production</small>
            <small>Ad Slot: {adSlot || 'Not configured'}</small>
          </div>
        </div>
      );
    }
    // In production, return null to avoid invalid ad requests
    return null;
  }

  return (
    <div className={`adsense-container ${className}`}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: style }}
        data-ad-client={adClientId}
        data-ad-slot={adSlot}
        data-ad-format={adFormat}
        data-full-width-responsive={fullWidthResponsive ? 'true' : 'false'}
      />
    </div>
  );
};

export default AdSense;
